import type { HeadersFunction, MetaFunction } from "react-router";
import { LoaderFunction, useLoaderData } from "react-router";
import AboutUs from "~/components/home-screen/AboutUs";
import Announcements from "~/components/home-screen/Announcements";
import DailyDarshanHome from "~/components/home-screen/DailyDarshanHome";
import Hero from "~/components/home-screen/Hero";
import LiveTvRadio from "~/components/home-screen/LiveTvRadio";
import type { RecentUpdate } from "~/components/home-screen/RecentUpdates";
import RecentUpdates from "~/components/home-screen/RecentUpdates";
import { OrganizationSchema } from "~/components/OrganizationSchema";
import { ArticleType } from "~/interface/article/article";
import { DarshanImage } from "~/interface/daily-darshan/daily-darshan";
import { HeroBanner, LiveData, NewsItem } from "~/interface/home/home";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { cn } from "~/lib/utils";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { orgSchemaProps } from "~/utils/organizationSchema";
import { ApiService } from "~/utils/service";

interface loaderdata {
  heroBannersData: HeroBanner[] | null;
  liveDataArray: LiveData[];
  newsData: NewsItem[];
  announcementData: ArticleType[];
  dailydarshanData: Record<string, DarshanImage[]>;
  availableDataDatesArray: string[];
  recentUpdate: RecentUpdate[];
  baseUrl: string;
}

// const BACKGROUND_IMAGE_STYLE = {
//   backgroundImage: `url("/images/background-image.png")`,
//   backgroundRepeat: "no-repeat",
//   height: "100%",
//   backgroundColor: "#fb8b241f",
// };

export const BACKGROUND_IMAGE_STYLE = {
  backgroundImage: `url("/images/background-image.png")`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover", // ← add this
  backgroundPosition: "center", // ← add this
  backgroundColor: "#fb8b241f",
};

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const { lang } = params;
  const meta = getPageMeta("home", lang || "en");

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage ?? FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth ?? "1200",
    ogHeight: meta.ogImageHeight ?? "630",
    canonicalLink: `${pathname}`,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const lang = params.lang;

  const apiService = new ApiService(lang || LANG.english);

  const response = await apiService.getHomeScreenData();
  const dailydarshanData = response?.daily_darshan || [];
  const heroBannersData = response?.hero_banners;
  const liveDataArray = response?.live_data;
  const newsData = response?.news;
  const recentUpdate = response?.latest_updates || [];
  const announcementData: ArticleType[] = response?.announcements || [];

  const etag = computeEtagFromData({
    heroBannersData,
    liveDataArray,
    newsData,
    announcementData,
    dailydarshanData,
    recentUpdate,
  });

  const ifNoneMatch = request.headers.get("If-None-Match");

  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(
    {
      heroBannersData: heroBannersData || [],
      liveDataArray,
      newsData,
      announcementData,
      dailydarshanData,
      recentUpdate,
    },
    {
      headers: {
        ...CACHED_PAGE.HOME.HEADERS,
        ETag: etag,
      },
    },
  );
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export default function Index() {
  const data = useLoaderData<loaderdata>();

  return (
    <div
      // w-full flex flex-col xs:gap-6 sm:gap-4 md:gap-6 justify-start items-center
      className="w-full flex flex-col justify-start items-center"
      style={{
        scrollBehavior: "auto",
        overflowAnchor: "none",
      }}
    >
      {/* Organization schema injected globally once */}
      <OrganizationSchema {...orgSchemaProps} />
      {/* Hero Section */}
      {data?.heroBannersData && data?.heroBannersData?.length > 0 && (
        <div className="body-container flex justify-start items-center hero_banner_pb">
          <Hero images={data?.heroBannersData} />
        </div>
      )}

      {/* Daily Darshan Section */}
      <div className="w-full bg-darkBackground flex justify-center items-center index_page_equal_p" style={BACKGROUND_IMAGE_STYLE}>
        <div className="body-container">
          {/* <Suspense fallback={<div className="w-full h-96 animate-pulse rounded-lg" />}> */}
          <DailyDarshanHome dailyDarshan={data?.dailydarshanData} />
          {/* </Suspense> */}
        </div>
      </div>

      {/* Live TV & Radio Section */}
      <div className="w-full flex justify-center items-center index_page_equal_p">
        {data?.liveDataArray && data?.liveDataArray.length > 0 && (
          <div className="relative body-container flex justify-center items-center overflow-hidden">
            {/* <Suspense fallback={<div className="w-full h-64 bg-darkBackground animate-pulse rounded-lg" />}> */}
            <LiveTvRadio data={data?.liveDataArray} />
            {/* </Suspense> */}
            <div
              className="absolute inset-0 w-full h-full opacity-100 -z-10 max-md:hidden flex-shrink-0"
              style={{
                backgroundImage: `url(/images/homepage-design.png)`,
                backgroundRepeat: "no-repeat",
              }}
            ></div>
          </div>
        )}
      </div>

      {/* Announcements Section */}
      {data.announcementData.length > 0 && (
        <div className="w-full flex justify-center items-center bg-darkBackground index_page_equal_p overflow-hidden" style={BACKGROUND_IMAGE_STYLE}>
          <div className="body-container flex justify-center items-center">
            {/* <Suspense fallback={<div className="w-full h-80 bg-darkBackground animate-pulse rounded-lg" />}> */}
            <Announcements data={data.announcementData} />
            {/* </Suspense> */}
          </div>
        </div>
      )}

      {/* Recent Updates Section */}
      {data.recentUpdate.length > 0 ? (
        <div className={cn("body-container index_page_equal_p px-3 flex justify-center items-center overflow-hidden")}>
          <div className="flex">
            {/* <Suspense fallback={<div className="w-full h-64 bg-darkBackground animate-pulse rounded-lg" />}> */}
            <RecentUpdates data={data.recentUpdate} />
            {/* </Suspense> */}
          </div>
        </div>
      ) : null}

      {/* About Us Section */}
      <div
        className={cn("w-full mx-auto flex justify-center items-center md:justify-center md:items-center px-3 overflow-hidden index_page_equal_p")}
        style={BACKGROUND_IMAGE_STYLE}
      >
        <div className="body-container flex flex-col justify-start items-start gap-8">
          <AboutUs />
        </div>
      </div>
    </div>
  );
}
